import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import SectionHeadline from 'shared/components/SectionHeadline';
import { ImageUploader } from 'shared/components/FileUploaders';
import { ColorPicker } from 'shared/components/ColorPickers';
import GroupOptions from 'shared/components/GroupOptions';
import { LabelError, LabelInput } from 'shared/components/Labels';
import CustomInput from 'shared/components/CustomInput';
import { colors } from 'shared/defaultTheme';
import { toBase64 } from 'shared/utils';

import { ReactComponent as TextFieldIcon } from 'assets/icon-text-field.svg';

const IconContainer = styled.div`
  height: 20px;
  width: 20px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
  padding-left: 4px;
  padding-right: 4px;
  svg {
    height: 100%;
    width: 100%;
  }
  margin-top: 4px;
`;

const BrandGraphicAspectionSection = ({
  mainColor,
  selectedLogoBase4,
  setSelectedLogoBase4,
  selectedLogoFooterBase4,
  setSelectedLogoFooterBase4,
  selectedFavicoBase4,
  setSelectedFavicoBase4,
  selectedMainColor,
  setSelectedMainColor,
  selectedSecondaryColor,
  setSelectedSecondaryColor,
  selectedPageHeaderOption,
  setSelectedPageHeaderOption,
  selectedPageFooterOption,
  setSelectedPageFooterOption,
  setTabTitle,
  tabTitle,
  socialReason,
  setSocialReason,
}) => {
  const { t } = useTranslation();

  const headerFooterPageOptions = [
    {
      name: t('general_customizer_option_bc_white_icon_main'),
      value: 1,
    },
    {
      name: t('general_customizer_option_bc_white_icon_black'),
      value: 2,
    },
    {
      name: t('general_customizer_option_bc_main_icon_white'),
      value: 3,
    },
    {
      name: t('general_customizer_option_bc_main_icon_black'),
      value: 4,
    },
  ];
  const [uploadedLogoError, setUploadedLogoError] = useState('');
  const [uploadedLogoFooterError, setUploadedLogoFooterError] = useState('');
  const [uploadedFaviconError, setUploadedFaviconError] = useState('');

  const onClickPageHeaderOption = (value) => setSelectedPageHeaderOption(value);
  const onClickPageFooterOption = (value) => setSelectedPageFooterOption(value);

  const onChange = (event, set) => {
    set(event.target.value);
  };

  const onChangeLogoFile = async (file) => {
    const curretLogoBase64 = await toBase64(file);
    setSelectedLogoBase4(curretLogoBase64);
  };

  const onSaveLogoBase64 = async (base64) => {
    setSelectedLogoBase4(base64);
  };

  const onChangeLogoFooterFile = async (file) => {
    const curretLogoBase64 = await toBase64(file);
    setSelectedLogoFooterBase4(curretLogoBase64);
  };

  const onSaveLogoFooterBase64 = async (base64) => {
    setSelectedLogoFooterBase4(base64);
  };

  const onChangeFaviconFile = async (file) => {
    const curretFileBase64 = await toBase64(file);
    setSelectedFavicoBase4(curretFileBase64);
  };

  const onSaveFaviconBase64 = async (base64) => {
    setSelectedFavicoBase4(base64);
  };

  const onErrorLogoUploading = (error) => {
    setUploadedLogoError(error);
  };

  const onErrorLogoFooterUploading = (error) => {
    setUploadedLogoFooterError(error);
  };

  const onErrorFaviconUploading = (error) => {
    setUploadedFaviconError(error);
  };

  return (
    <>
      <SectionHeadline
        className="general-customizer__section-headline-1"
        sectionNumber={1}
        label={t('general_customizer_section_brand')}
        color={mainColor}
      />
      <ImageUploader
        className="general-customizer__logo-uploader"
        mainColor={mainColor}
        label={t('general_customizer_label_color_logo')}
        labelClassName="text-uppercase"
        labelColor={mainColor}
        required
        imageBase64={selectedLogoBase4}
        onChangeFile={onChangeLogoFile}
        onSaveBase64={onSaveLogoBase64}
        onError={onErrorLogoUploading}
      />
      {uploadedLogoError && <LabelError label={uploadedLogoError} />}
      <ImageUploader
        className="general-customizer__logo-uploader"
        mainColor={mainColor}
        label={t('general_customizer_label_white_logo')}
        labelClassName="text-uppercase"
        labelColor={mainColor}
        required
        imageBase64={selectedLogoFooterBase4}
        onChangeFile={onChangeLogoFooterFile}
        onSaveBase64={onSaveLogoFooterBase64}
        onError={onErrorLogoFooterUploading}
      />
      {uploadedLogoFooterError && <LabelError label={uploadedLogoFooterError} />}
      <ImageUploader
        className="general-customizer__logo-uploader"
        mainColor={mainColor}
        label={t('general_customizer_label_favicon')}
        labelClassName="text-uppercase"
        labelColor={mainColor}
        required
        imageBase64={selectedFavicoBase4}
        onChangeFile={onChangeFaviconFile}
        onSaveBase64={onSaveFaviconBase64}
        onError={onErrorFaviconUploading}
      />
      {uploadedFaviconError && <LabelError label={uploadedFaviconError} />}
      <ColorPicker
        className="general-customizer__color-pickers"
        label={t('general_customizer_label_main_color')}
        labelColor={mainColor}
        labelClass="body-text--secondary text-uppercase"
        required
        selectedColor={selectedMainColor}
        onChange={setSelectedMainColor}
      />
      <ColorPicker
        className="general-customizer__color-pickers"
        label={t('general_customizer_label_secondary_color')}
        labelColor={mainColor}
        labelClass="body-text--secondary text-uppercase"
        required
        selectedColor={selectedSecondaryColor}
        onChange={setSelectedSecondaryColor}
      />
      <GroupOptions
        className="general-customizer__page-header-otions"
        title={t('general_customizer_label_design_header_page')}
        titleClassName="text-uppercase"
        color={mainColor}
        options={headerFooterPageOptions}
        onClick={onClickPageHeaderOption}
        selectedOption={selectedPageHeaderOption}
      />
      <GroupOptions
        className="general-customizer__page-footer-otions"
        title={t('general_customizer_label_design_footer_page')}
        titleClassName="text-uppercase"
        color={mainColor}
        options={headerFooterPageOptions}
        onClick={onClickPageFooterOption}
        selectedOption={selectedPageFooterOption}
      />
      <div className="general-customizer__social-network-wrapper">
        <div className="mb-4">
          <LabelInput className="text-uppercase" label={t('general_title_tab')} color={mainColor} />
          <CustomInput
            type="url"
            Icon={() => (
              <IconContainer backgroundColor="transparent">
                <TextFieldIcon fill={colors.suvaGrey} />
              </IconContainer>
            )}
            value={tabTitle}
            onChange={(event) => onChange(event, setTabTitle)}
          />
        </div>

        <LabelInput
          className="text-uppercase"
          label={t('general_business_name')}
          color={mainColor}
        />
        <CustomInput
          type="url"
          Icon={() => (
            <IconContainer backgroundColor="transparent">
              <TextFieldIcon fill={colors.suvaGrey} />
            </IconContainer>
          )}
          value={socialReason}
          onChange={(event) => onChange(event, setSocialReason)}
        />
      </div>
    </>
  );
};

BrandGraphicAspectionSection.defaultProps = {
  mainColor: undefined,
  selectedLogoBase4: undefined,
  setSelectedLogoBase4: undefined,
  selectedLogoFooterBase4: undefined,
  setSelectedLogoFooterBase4: undefined,
  selectedFavicoBase4: undefined,
  setSelectedFavicoBase4: undefined,
  selectedMainColor: undefined,
  setSelectedMainColor: undefined,
  selectedSecondaryColor: undefined,
  setSelectedSecondaryColor: undefined,
  selectedPageHeaderOption: undefined,
  setSelectedPageHeaderOption: undefined,
  selectedPageFooterOption: undefined,
  setSelectedPageFooterOption: undefined,
  socialReason: '',
};

BrandGraphicAspectionSection.propTypes = {
  mainColor: PropTypes.string,
  selectedLogoBase4: PropTypes.string,
  setSelectedLogoBase4: PropTypes.func,
  selectedLogoFooterBase4: PropTypes.string,
  setSelectedLogoFooterBase4: PropTypes.func,
  selectedFavicoBase4: PropTypes.string,
  setSelectedFavicoBase4: PropTypes.func,
  selectedMainColor: PropTypes.string,
  setSelectedMainColor: PropTypes.func,
  selectedSecondaryColor: PropTypes.string,
  setSelectedSecondaryColor: PropTypes.func,
  selectedPageHeaderOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedPageHeaderOption: PropTypes.func,
  selectedPageFooterOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedPageFooterOption: PropTypes.func,
  tabTitle: PropTypes.string.isRequired,
  setTabTitle: PropTypes.func.isRequired,
  socialReason: PropTypes.string,
  setSocialReason: PropTypes.func.isRequired,
};

export default BrandGraphicAspectionSection;
